import { Alert, Autocomplete, FormControl, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import InputHeader from '../../../../components/InputHeader';
import { useLanguage } from '../../../../contexts/language';
import { SelectItem } from '../../../../types/core';
import { JourneyTemplateSubPageContext } from '../types';
import { CrewMemberSelectItem, JourneyLogFormValues } from './types';
import VesselCertificatesAlert from './VesselCertificatesAlert';
import { UseIotsAdminFormResult } from '../../../../hooks/useIotsAdminForm';
export interface PassengersFormProps {
  formManager: UseIotsAdminFormResult<JourneyLogFormValues>;
  crewMembers: CrewMemberSelectItem[];
  crewMembersMap: Record<string, CrewMemberSelectItem>;
  isFetchingCrewMembers: boolean;
  validationMessages: string[];
  carriesPassengers: boolean;
  vesselsMap: Record<string, SelectItem>;
}

const PassengersForm = ({
  formManager,
  crewMembers,
  crewMembersMap,
  isFetchingCrewMembers,
  validationMessages,
  carriesPassengers,
  vesselsMap,
}: PassengersFormProps) => {
  const { t } = useLanguage();
  const { journey } = useOutletContext<JourneyTemplateSubPageContext>();
  const {
    formState,
    materialInputHelpers: { getBaseProps, getTextFieldProps, renderError },
  } = formManager;

  const selectedVesselId = formState.values.vesselId;

  const { allErrors, values } = formState;
  const { crewMembersAmount, passensgersAmount } = useMemo(() => {
    const crewMembersAmount =
      (values.skipperId ? 1 : 0) + (values.crewMemberIds ?? []).length;

    const fields: Array<keyof JourneyLogFormValues> = [
      'adultIslanders',
      'adultNonIslanders',
      'under18Islanders',
      'under18NonIslanders',
      'under05Islanders',
      'under05NonIslanders',
      'studentIslanders',
      'studentNonIslanders',
      'over65Islanders',
      'over65NonIslanders',
    ];
    const passengersAmount = fields.reduce(
      (acc, field) => acc + (!allErrors[field] ? Number(values[field]) : 0),
      0,
    );
    return { crewMembersAmount, passensgersAmount: passengersAmount };
  }, [allErrors, values]);

  const passengerNumericFields = useMemo<
    Array<[string, keyof JourneyLogFormValues, keyof JourneyLogFormValues]>
  >(
    () => [
      [t('content.adults'), 'adultIslanders', 'adultNonIslanders'],
      [t('content.under-18'), 'under18Islanders', 'under18NonIslanders'],
      [t('content.under-5'), 'under05Islanders', 'under05NonIslanders'],
      [t('content.students'), 'studentIslanders', 'studentNonIslanders'],
      [t('content.over-65'), 'over65Islanders', 'over65NonIslanders'],
    ],
    [t],
  );

  const vesselCertificatesAlert =
    selectedVesselId && vesselsMap[selectedVesselId] ? (
      <VesselCertificatesAlert
        selectedVesselId={selectedVesselId}
        journey={journey}
        vesselName={vesselsMap[selectedVesselId]?.label}
      />
    ) : null;

  return (
    <div>
      {validationMessages.map(message => (
        <Alert key={message} className="mb-2" color="warning">
          {message}
        </Alert>
      ))}
      {vesselCertificatesAlert}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 auto-rows-auto">
        <header className="mb-2 col-span-full font-bold">
          {`${t('content.crew')} (${crewMembersAmount})`}
        </header>
        <label className="mb-4 col-span-full">
          <InputHeader required>{t('content.skipper')}</InputHeader>
          <FormControl error={getBaseProps('skipperId').error}>
            <Autocomplete<CrewMemberSelectItem>
              value={crewMembersMap[formState.values.skipperId ?? ''] ?? null}
              onChange={(_event, selectedOption) => {
                const newSkipperId = selectedOption?.value ?? null;
                if (
                  newSkipperId &&
                  formState.values.crewMemberIds.includes(newSkipperId)
                ) {
                  formState.updateValues(
                    {
                      skipperId: newSkipperId,
                      crewMemberIds: formState.values.crewMemberIds.filter(
                        id => id !== newSkipperId,
                      ),
                    },
                    true,
                  );
                } else {
                  formState.updateValues({ skipperId: newSkipperId }, true);
                }
              }}
              options={crewMembers.filter(({ isSkipper }) => isSkipper)}
              onBlur={getBaseProps('skipperId').onBlur}
              loading={isFetchingCrewMembers}
              disabled={
                getBaseProps('skipperId').disabled || isFetchingCrewMembers
              }
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('skipperId').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionLabel={option => option.label}
            />
            {renderError('skipperId')}
          </FormControl>
        </label>
        <label className="mb-4 col-span-full">
          <InputHeader>{t('content.other')}</InputHeader>
          <FormControl error={getBaseProps('crewMemberIds').error}>
            <Autocomplete<CrewMemberSelectItem, true>
              multiple
              disableCloseOnSelect={true}
              value={formState.values.crewMemberIds.reduce((acc, id) => {
                const crewMember = crewMembersMap[id];
                if (crewMember) {
                  acc.push(crewMember);
                }
                return acc;
              }, [] as CrewMemberSelectItem[])}
              onChange={(
                _event: React.SyntheticEvent,
                selectedOptions: CrewMemberSelectItem[],
              ) => {
                const newCrewMemberIds = selectedOptions.map(opt => opt.value);
                formState.updateValues(
                  {
                    crewMemberIds: newCrewMemberIds,
                  },
                  true,
                );
              }}
              options={crewMembers}
              onBlur={getBaseProps('crewMemberIds').onBlur}
              loading={isFetchingCrewMembers}
              disabled={
                getBaseProps('crewMemberIds').disabled || isFetchingCrewMembers
              }
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('crewMemberIds').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionKey={option => option.value}
              getOptionLabel={option => option.label}
              getOptionDisabled={option =>
                option.value === formState.values.skipperId
              }
            />
            {renderError('crewMemberIds')}
          </FormControl>
        </label>
        {carriesPassengers && (
          <>
            <header className="mb-2 col-span-full font-bold">
              {`${t('content.passengers')} (${passensgersAmount})`}
            </header>
            {passengerNumericFields.map(
              ([label, islandersKey, nonIslandersKey]) => (
                <div
                  key={label}
                  className="col-span-full row-span-5 sm:row-span-3 grid grid-cols-subgrid grid-rows-subgrid"
                >
                  <InputHeader required className="col-span-full">
                    {label}
                  </InputHeader>
                  <label className="mb-4 row-span-2 grid grid-rows-subgrid">
                    <header className="text-xs">
                      {t('content.islanders')}
                    </header>
                    <TextField {...getTextFieldProps(islandersKey)} type='number'/>
                  </label>
                  <label className="mb-4 row-span-2 grid grid-rows-subgrid">
                    <header className="text-xs">
                      {t('content.non-islanders')}
                    </header>
                    <TextField {...getTextFieldProps(nonIslandersKey)} type='number' />
                  </label>
                </div>
              ),
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PassengersForm;
